import { Box, Collapse } from '@mui/material';
import { useStyles } from './Medications.styles';
import { iconBtnPosition, iconBtnType, IconButton } from '../../../../molecules/icon-button';
import clsx from 'clsx';
import { Menu } from '../../../../molecules/menu';
import LottieMedications from '../../../../../icons/lotties/lottieIcons/LottieMedications';
import { sentenceCase } from 'sentence-case';
import { useSelector } from 'react-redux';
import { getAuth } from '../../../../../../redux/modules/auth/selectors';
import React, { useEffect, useState } from 'react';
import { fetchNavItemStatus } from '../../../../../../services/datalab/datalab.service';
import CheckPDMP from '../../../../../../components/v2/CheckPDMP';
import { typography } from '../../../../../../components/v2/Typography/index.constant';
import Typography from '../../../../../../components/v2/Typography';
import { colors } from '../../../../../colors';
import dayjs from '../../../../../../utils/dayjs';
import { getImportanceLevelDisplayText } from '../../../../../../utils/medicationUtils';

const Medications = ({
  patientState,
  onAddClick,
  medications,
  onUpdate,
  uuid,
  pdmpSummary,
  pdmpLastUpdated,
}) => {
  const classes = useStyles();
  const [hasEPrescribeAccess, setHasEPrescribeAccess] = useState(false);
  const [expandedMedicationId, setExpandedMedicationId] = useState(null);
  const {
    meta: { userId },
  } = useSelector(getAuth);

  useEffect(() => {
    const checkMDTCreds = async () => {
      const status = await fetchNavItemStatus({ providerId: userId });
      const statusData = status?.data?.data;
      if (!statusData || !statusData['MDToolboxAccess']) {
        setHasEPrescribeAccess(false);
      } else {
        setHasEPrescribeAccess(true);
      }
    };
    void checkMDTCreds();
  }, []);

  const renderPDMP = () => {
    return (
      <CheckPDMP
        patientState={patientState}
        uuid={uuid}
        summary={pdmpSummary}
        lastUpdated={pdmpLastUpdated}
      />
    );
  };

  const openCareNavigatorChat = () => {
    window.open('/provider/chats?type=care_navigator', '_blank');
  };

  const renderEmptyState = () => {
    return (
      <Box display='flex' flexDirection='column' gap='16px'>
        <Box display='flex' gap='8px' alignItems='center'>
          <Box width={80} maxWidth={80} maxHeight={80}>
            <LottieMedications />
          </Box>
          <Box display='flex' flexDirection='column' gap='4px'>
            <span className={classes.bodyUuid}>
              {hasEPrescribeAccess ? 'Now prescribe directly in Confidant' : 'Prescribing?'}
            </span>
            <span className={classes.bodyServiceName}>
              {hasEPrescribeAccess ? (
                <>
                  <span onClick={onAddClick} className={classes.inlineLink}>
                    Add your medication here
                  </span>{' '}
                  and we'll submit it directly to the pharmacy.
                </>
              ) : (
                <>
                  To activate direct e-prescribe,{' '}
                  <span onClick={openCareNavigatorChat} className={classes.inlineLink}>
                    chat with the Care Navigation Team
                  </span>
                  .
                </>
              )}
            </span>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderDetailProperty = (label, value, avoidSentenceCase) => {
    return (
      <Box display='flex' flexDirection='row' alignItems='center' gap='16px'>
        <Typography {...typography.body.s.medium} color={colors.neutral500}>
          {label}
        </Typography>
        <Typography
          sx={{
            display: 'flex',
            flex: 1,
            height: 20,
            minWidth: 50,
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='20' width='12'%3E%3Ccircle cx='10' cy='10' r='1' fill='%23bec9d3' /%3E%3C/svg%3E ")`,
          }}
          {...typography.body.s.medium}
        />
        <Typography {...typography.body.s.medium} color={colors.neutral700}>
          {avoidSentenceCase ? value : sentenceCase(value)}
        </Typography>
      </Box>
    );
  }

  const renderMedicationCard = () => {
    return (
      <Box display='flex' flexDirection='column' gap='12px' width='100%' height='100%'>
        {medications.map((item, index) => {
          const isExpanded = expandedMedicationId === item.id;
          const isExternalRx = 
            item.currentImportanceLevel.toLowerCase().includes('external');
          
          // Filter menu items - hide View More/Less for external RX
          const menuItems = [
            !isExternalRx && {
              label: `View ${isExpanded ? 'Less' : 'More'}`,
              onClick: (ev) => {
                setExpandedMedicationId(isExpanded ? null : item.id);
              },
            },
            {
              label: 'Update Medication',
              onClick: () => onUpdate(item),
            },
          ].filter(Boolean);
          
          return (
            <Box key={index} className={classes.card}
                 onClick={() => !isExternalRx && setExpandedMedicationId(isExpanded ? null : item.id)}>
              <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems='center'>
                <Box display='flex' flexDirection='column' gap='8px' width='100%'>
                  <Box display='flex' alignItems='center' gap='12px'>
                    <Box className={classes.cardTitle}>
                      {isExternalRx ? (item.note || item.tagMetaData?.rxDrugInfo?.additionalNotes || '') : item.title}
                    </Box>
                    <Box className={classes.badgeSuccess}>{getImportanceLevelDisplayText(item.status)}</Box>
                  </Box>
                  <Box className={classes.cardSubTitle}>
                    {item.sig && (
                      <>
                        {sentenceCase(item.sig)}
                        <br />
                      </>
                    )}
                    {item.updatedAt && <>Last RX: {dayjs(item.updatedAt).format('MM/DD/YY')}</>}
                  </Box>
                </Box>
                <Menu
                  icon='more'
                  onMenuClick={(e) => e.stopPropagation()}
                  items={menuItems}
                />
              </Box>
              {!isExternalRx && (
                <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                  <Box className={classes.drugDetailsBox}>
                    <Box className={classes.detailHr} />
                    <Box display="flex" flexDirection="column" gap="16px">
                      {item.drug && renderDetailProperty('Drug', item.drug, false)}
                      {item.dose && renderDetailProperty('Dose', item.dose, false)}
                      {item.frequency && renderDetailProperty('Frequency', item.frequency, true)}
                      {item.duration && renderDetailProperty('Duration', `${item.duration} Days`, false)}
                      {item.quantity && renderDetailProperty('Quantity', item.quantity + "", false)}
                      {renderDetailProperty('Refill', item.refills || 0, true)}
                      {item.dispenseAmount && renderDetailProperty('Dispense Amount', `${item.dispenseAmount || 0} ${item.dose}${item.dispenseAmount > 0 ? 's' : ''}`, true)}
                      {item.selectedIct10code && renderDetailProperty('Associated Diagnosis', item.selectedIct10code, false)}
                      {renderDetailProperty('Allow Substitutions', item.allowSubstitutions ? 'Yes' : 'No', false)}
                      {item.pharmacyName && renderDetailProperty('Pharmacy', `${item.pharmacyName} - ${item.pharmacyZipCode}`, true)}
                    </Box>
                  </Box>
                </Collapse>
              )}

            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      {renderPDMP()}
      <Box className={classes.medicationsContainer}>
        <Box className={classes.header}>
          <Box>Active Medications</Box>
          <Box>
            <IconButton
              className={classes.addBtn}
              icon='plus'
              variant={iconBtnType.TEXT}
              onClick={onAddClick}
              iconPosition={iconBtnPosition.LEFT}
            >
              Add new
            </IconButton>
          </Box>
        </Box>
        <Box
          className={clsx(classes.body, {
            [classes.medicationBody]: medications.length !== 0,
            [classes.p24]: medications.length === 0,
          })}
        >
          {medications.length === 0 ? renderEmptyState() : renderMedicationCard()}
        </Box>
      </Box>
    </Box>
  );
};
export { Medications };
